import React, { MouseEventHandler, useEffect, useRef } from 'react';
import { Box, IconProps, BubbleProps } from '@carta/ink';
import { SideNavItemContent } from './SideNavItemContent';

export interface SideNavItemProps extends React.HTMLAttributes<HTMLAnchorElement> {
  /** Link title text */
  title: string;
  /** URL to navigate to */
  url?: string;
  /** Active state modifier */
  isActive?: boolean;
  /** Visible state modifier (for nested items in expandable groups) */
  isVisible?: boolean;
  /** Nav item icon type */
  imageIcon?: IconProps['type'];
  /** Sticky state modifier */
  sticky?: boolean;
  /** Hides the vertical stripe in active items */
  noStripe?: boolean;
  /** Open link in new tab */
  openInNewTab?: boolean;
  /** Use React Router Link element instead of native anchor */
  withReactRouter?: boolean;
  /** Nesting level for indentation (0 = root, 1 = first level, 2 = second level) */
  nestingLevel?: 0 | 1 | 2;
  /** onClick callback, sends the event */
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
  /** Bubble type and text */
  bubble?: { type?: BubbleProps['type']; text: string | number } | null;
}

/**
 * This component consolidates the original `SideNavItem` and `SideNavGroupItem` to render each accordingly
 * @purpose Navigation list item element
 */
const SideNavItem = React.forwardRef<HTMLAnchorElement, SideNavItemProps>((props, ref) => {
  const { sticky, isVisible } = props;

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const itemElementRef = useRef<HTMLAnchorElement | null>(null);

  // isVisible indicates item should be a SideNavGroupItem
  const asGroupItem = isVisible !== undefined;

  // From original SideNavGroupItem
  const mergedRef = React.useCallback(
    (node: HTMLAnchorElement | null) => {
      itemElementRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<HTMLAnchorElement | null>).current = node;
      }
    },
    [ref],
  );

  // From original SideNavGroupItem
  useEffect(() => {
    if (!props.isActive || (isVisible !== undefined && !isVisible)) return;
    const itemElement = itemElementRef.current;
    if (itemElement) {
      // @ts-ignore error needed as for some reason TS says 'instant' isn't an option for behaviour even though the docs say otherwise
      itemElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'start' });
    }
  }, [props.isActive, isVisible]);

  const content = <SideNavItemContent {...(props as any)} ref={mergedRef} asGroupItem={asGroupItem} />;

  // SideNavGroupItem -> li
  if (asGroupItem) {
    return <li>{content}</li>;
  }

  // SideNavItem -> Box
  return <Box minWidth={sticky ? 'stretch' : undefined}>{content}</Box>;
});

SideNavItem.displayName = 'SideNav.Item';

export default SideNavItem;
